"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/result/charts/RunwayBarChart";

import type { FC } from "react";
import type { RunwayBarsChartPayload } from "../types";

const I18n = I18nUtil.createScoped(Translations);

interface RunwayBarChartProps {
  data: RunwayBarsChartPayload;
}

const BAR_TRACK_MAX_WIDTH_PX = 235;

const barWidth = (months: number, max: number): string => {
  if (max <= 0) {
    return "0%";
  }
  return `${Math.min(100, (months / max) * 100)}%`;
};

interface RunwayBarProps {
  months: number;
  maxMonths: number;
  barClassName: string;
  testId: string;
}

/** Figma 96344:110758 — label sits inside the colored bar, right-aligned. */
const RunwayBar: FC<RunwayBarProps> = ({
  months,
  maxMonths,
  barClassName,
  testId,
}) => (
  <div
    className="w-full"
    style={{ maxWidth: BAR_TRACK_MAX_WIDTH_PX }}
    data-testid={`${testId}-track`}
  >
    <div
      className={`flex h-[30px] min-w-[52px] items-center justify-end rounded-md px-2 ${barClassName}`}
      data-testid={testId}
      data-runway-months={months}
      style={{ width: barWidth(months, maxMonths) }}
    >
      <span className="body_bold_s whitespace-nowrap text-content_inverse_primary">
        {`${months} mo`}
      </span>
    </div>
  </div>
);

export const RunwayBarChart: FC<RunwayBarChartProps> = ({ data }) => {
  const maxMonths = Math.max(data.current, data.safeTarget, data.withEd, 1);

  return (
    <div className="w-full py-2" data-testid="result-runway-chart">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <p className="body_medium_s text-content_primary">
            {I18n.t("On your own")}
          </p>
          <RunwayBar
            months={data.current}
            maxMonths={maxMonths}
            barClassName="bg-content_tertiary"
            testId="result-runway-bar-own"
          />
        </div>
        <div className="flex flex-col gap-1">
          <p className="body_medium_s text-content_brand_primary">
            {I18n.t("With Ed")}
          </p>
          <RunwayBar
            months={data.withEd}
            maxMonths={maxMonths}
            barClassName="bg-content_brand_primary"
            testId="result-runway-bar-ed"
          />
        </div>
      </div>
      <p className="body_medium_s mt-2 text-content_tertiary">
        {`Safe cushion target: ${data.safeTarget} months`}
      </p>
    </div>
  );
};
